import range from 'zycommon-web/utils/range';

// Notation formats
export const decimalNumber = '(\\+|-)?((\\d*\\.?\\d+)|(\\d+\\.?\\d*))';
// Matches E notation in three formats:
// 1. \mathrm{EDIGITS} - physical keyboard (argument inside mathrm braces)
// 2. \mathrm{E}{DIGITS} - legacy braced format (argument in separate braces)
// 3. \mathrm{E}DIGITS - virtual keyboard (argument outside braces, unbraced)
export const eNotationLatexGetArgument = /\\mathrm\{E([+-]?\d+)\}|\\mathrm\{E\}\{([+-]?\d+)\}|\\mathrm\{E\}([+-]?\d+)/gu;

// E notation constants
export const eNotationWithPlaceholder = '\\mathrm{E}\\placeholder{}';
const eNotationContentAfter = /\\mathrm\{E\}\{(.*)\}(\w+)/u;
const eNotationGoneOnlyPlaceholder = /(?<![}{])\\placeholder\{\}/u;

// Matches \mathrm{E} with no valid argument (no digit, no braced content, no placeholder)
const eNotationMissingArgument = /\\mathrm\{E\}(?![\d{]|\\placeholder|\+|-)/u;

// Scientific notation constants
export const scientificNotationWithPlaceholder = '\\times10^{\\placeholder{}}';
const scientificNotationContentAfter = /\\times10([^^]+|$)/u;
const scientificNotationNoExponent = /\\times10([^^]|$)/u;
const scientificNotationGoneOnlyPlaceholder = /(?<![}^])\{\\placeholder\{\}\}/u;

// Matches orphaned braced argument when E or ^ is deleted
const eOrScientificNotationsGoneOnlyArgument = /(^|[^^}]+)\{([^}]+)\}/u;

export const eNotationDeleteScenarios = [
    // E's argument was deleted
    {
        isScenarioPresent: value => eNotationMissingArgument.test(value),
        handleScenario: (editor, previousValue) => {
            const { index } = editor.value.match(eNotationMissingArgument);
            const previousValueFromChange = previousValue.slice(index);
            const hadPlaceholderPreviously =
                previousValueFromChange.startsWith('\\mathrm{E}{\\placeholder{}}') ||
                previousValueFromChange.startsWith('\\mathrm{E}\\placeholder{}');

            // Deleting placeholder removes E
            if (hadPlaceholderPreviously) {
                editor.executeCommand('deleteBackward');
            }

            // Deleting argument replaces with a placeholder
            else {
                editor.executeCommand(['insert', '{\\placeholder{}}']);
            }
        },
    },

    // Deleting E also removes orphaned \placeholder{}
    {
        isScenarioPresent: value => eNotationGoneOnlyPlaceholder.test(value),
        handleScenario: editor => editor.executeCommand('deleteForward'),
    },
];

export const scientificNotationDeleteScenarios = [
    // \times10's exponent was deleted
    {
        isScenarioPresent: value => scientificNotationNoExponent.test(value),
        handleScenario: (editor, previousValue) => {
            const { index } = editor.value.match(scientificNotationNoExponent);
            const hadPlaceholderPreviously = previousValue.slice(index).startsWith('\\times10^{\\placeholder{}}');

            // Deleting placeholder removes \times10
            if (hadPlaceholderPreviously) {
                // Remove the 0
                editor.executeCommand('deleteBackward');

                // Remove the 1
                editor.executeCommand('deleteBackward');

                // Remove the \times
                editor.executeCommand('deleteBackward');
            }

            // Deleting argument replaces with a placeholder
            else {
                editor.executeCommand(['insert', '^{\\placeholder{}}']);
            }
        },
    },

    // Deleting ^ also removes orphaned {\placeholder{}}
    {
        isScenarioPresent: value => scientificNotationGoneOnlyPlaceholder.test(value),
        handleScenario: editor => editor.executeCommand('deleteForward'),
    },
];

// Deleting \times10^'s' or ^'s exponent adds placeholder
export const addPlaceholderToEmptySuperscript = {
    isScenarioPresent: value => value.includes('^{}'),
    handleScenario: editor => editor.executeCommand(['insert', '\\placeholder{}']),
};

export const generalDeleteScenarios = [
    addPlaceholderToEmptySuperscript,

    // Deleting \E or \times10^ removes braces around the former second argument's content
    {
        isScenarioPresent: value => eOrScientificNotationsGoneOnlyArgument.test(value),
        handleScenario: editor => {
            const [, before, argument] = editor.value.match(/(^|[^}]+)\{([^}]+)\}/u);

            if (!before.includes('\\mathrm')) {
                // Remove the former second argument
                editor.executeCommand('deleteForward');

                // Re-add the former second argument without braces
                editor.executeCommand(['insert', argument]);

                // Move cursor to front of former second argument
                argument.split('').forEach(() => {
                    editor.executeCommand('moveToPreviousChar');
                });
            }
        },
    },
];

export const eNotationInsertScenarios = [
    // Move content inserted to right of E into argument
    {
        isScenarioPresent: value => eNotationContentAfter.test(value),
        handleScenario: editor => {
            const [, argument, after] = editor.value.match(eNotationContentAfter);

            // Remove the |after|
            after.split('').forEach(() => {
                editor.executeCommand('deleteBackward');
            });

            // Move caret into argument
            editor.executeCommand('moveToPreviousWord');

            // Clear the placeholder
            if (argument === '\\placeholder{}') {
                editor.executeCommand('deleteBackward');
            }

            // Re-add the |after|
            editor.executeCommand(['insert', after]);
        },
    },
];

export const scientificNotationInsertScenarios = [
    // Handle a keyboard-entered x: Insert |scientificNotationWithPlaceholder| or just \times
    {
        isScenarioPresent: value => value.includes('\\Keyboardx'),
        handleScenario: editor => {
            const result = editor.value.match(/\\Keyboardx(\\;)*\d/u);

            // Remove Keyboardx
            editor.executeCommand('deleteBackward');

            let toInsert = scientificNotationWithPlaceholder;

            if (result) {
                toInsert = '\\times';

                // Remove any \; after Keyboardx
                const numSpaces = result[0].match(/\\;/gu)?.length ?? 0;

                range(numSpaces).forEach(() => {
                    editor.executeCommand('deleteForward');
                });
            }
            editor.executeCommand(['insert', toInsert]);
        },
    },

    // Create the exponent with content after \times10 when \times10 doesn't have an exponent
    {
        isScenarioPresent: value => scientificNotationContentAfter.test(value),
        handleScenario: editor => {
            const [, after] = editor.value.match(scientificNotationContentAfter);

            // Remove the |after|
            after.split('').forEach(() => {
                editor.executeCommand('deleteBackward');
            });

            // Add a second argument with |after|
            const exponent = after.length === 1 ? after : `{${after}}`;

            editor.executeCommand(['insert', `^${exponent}`]);
        },
    },
];

export const negationInsertScenarios = [
    // Negate the number, toggling between - and nothing
    {
        isScenarioPresent: value => value.includes('\\Negate'),
        handleScenario: editor => {
            // Handle unbraced E notation: \mathrm{E}\Negate followed by placeholder or digit (not {)
            // Ex: 9\mathrm{E}\Negate\placeholder{} or 9\mathrm{E}\Negate3
            const unbracedENotationNegate = /\\mathrm\{E\}(-?)\\Negate/u;

            if (unbracedENotationNegate.test(editor.value)) {
                const [, existingSign] = editor.value.match(unbracedENotationNegate);

                // Remove \Negate
                editor.executeCommand('deleteBackward');

                if (existingSign === '-') {
                    // Already negative, remove the sign (toggle off)
                    editor.executeCommand('deleteBackward');
                } else {
                    // Not negative, add the sign (toggle on)
                    editor.executeCommand(['insert', '-']);
                }
                return;
            }

            // Negate added to front of mathrm{E}{...} actually puts negate between {E} and {...}
            // Instead, move that \\Negate into the {...}
            if (/\\mathrm\{E\}\\Negate\{/u.test(editor.value)) {
                // Removed negate
                editor.executeCommand('deleteBackward');

                // Move caret into argument
                editor.executeCommand('moveToNextWord');

                // Insert negate into {...}
                editor.executeCommand(['insert', '\\Negate']);
            }

            // Find the number to negate
            const matchNumberInParens = editor.value.match(/\{([^}]*\\Negate[^}]*)\}/u);
            const signAndNumber = matchNumberInParens?.[1] ?? editor.value;

            // Remove negate
            editor.executeCommand('deleteBackward');

            // Record position of cursor without negate b/c that's where we'll need to return to
            const oldCursorPosition = editor.position;

            // Move to start of number
            editor.executeCommand('moveToGroupStart');

            // Delete the old sign
            const [, negateAtStart, sign] = signAndNumber.match(/^(\\Negate)?(\+|-)?/u);

            if (sign) {
                editor.executeCommand('deleteForward');
            }

            // Add the new sign
            const addNegative = sign !== '-';

            if (addNegative) {
                editor.executeCommand(['insert', '-']);
            }

            // Add a placeholder if nothing in parens
            const addPlaceholder = matchNumberInParens && ['\\Negate-', '-\\Negate'].includes(signAndNumber);

            if (addPlaceholder) {
                editor.executeCommand(['insert', '\\placeholder{}']);
            }

            // Move cursor back
            else {
                const addSign = !sign && addNegative;
                const removeSign = sign && !addNegative;
                let offset = 0;

                if (addSign) {
                    offset = 1;
                } else if (removeSign && !negateAtStart) {
                    offset = -1;
                }

                editor.position = oldCursorPosition + offset;
            }
        },
    },
];

// Drop out of exponents when not a number (-, ., \d) is entered. Ex: Type 1, x, -, 2, N, *, m, /, A to yield 1\times10^{-2}\SN\cdot\Sm/\SA
const exponentOrENotationEndingWithNonNumber = /(\^|\\mathrm\{E\})\{([-.\d]*)([^-.\d}]+)\}/u;
export const nonNumberInExponentScenario = {
    isScenarioPresent: value => exponentOrENotationEndingWithNonNumber.test(value),
    handleScenario: editor => {
        const [, , number, nonNumber] = editor.value.match(exponentOrENotationEndingWithNonNumber);

        if (!nonNumber.includes('\\placeholder{')) {
            // Remove non-number
            editor.executeCommand('deleteBackward');

            // Add placeholder if nothing else in exponent
            if (!number) {
                editor.executeCommand(['insert', '\\placeholder{}']);
            }

            // Move out of exponent
            editor.executeCommand('moveToNextChar');

            // Re-add non-number, if not just a space
            if (nonNumber !== '\\;') {
                editor.executeCommand(['insert', nonNumber]);
            }
        }
    },
};

export const halfHeightSeparator = { class: 'separator half-height' };

/**
 * Align buttons in rows by making each row the same length as the longest row.
 * @param {Array} rows The rows of the keyboard layout as a 2D array.
 * @returns {void}
 */
export function alignButtonsInRows(rows) {
    // Make each row the same length as the longest row
    const longestRow = Math.max(...rows.map(({ length }) => length));

    rows.forEach((row, index) => {
        row.push(...Array(longestRow - row.length).fill(index === 0 ? halfHeightSeparator : '[separator]'));
    });
}

/**
 * Evaluate the given latex as a number.
 * @param {string} latex The number as latex.
 * @returns {number}
 */
export function evaluateLatexNumber(latex) {
    // A decimal number without a digit after the decimal is not understood by the engine.
    const numberLatex = latex.replaceAll(/(\d+)\.(?:[^\d]|$)/gu, (match, number) => number);

    return new window.ComputeEngine.ComputeEngine().parse(numberLatex).evaluate().valueOf();
}

/**
 * Remove the block notation in the given latex. A copy/paste may yield $ ... $.
 * @function removeBlockNotation
 * @param {string} latex The given latex.
 * @returns {string}
 */
export function removeBlockNotation(latex) {
    const blockNotation = /^\$\$ +(.*) +\$\$$/u;

    return latex.replace(blockNotation, override => override.match(blockNotation)[1]);
}

/**
 * Get the delete scenarios for the Symbolic Number and Number and Unit symtools.
 * The order in which the scenarios are added determines their precedence.
 * @param {boolean} accept_E_notation Whether to accept E notation.
 * @param {boolean} supportScientificNotation Whether to support scientific notation.
 * @returns {Array} The common delete scenarios.
 */
export function getCommonNumberDeleteScenarios(accept_E_notation, supportScientificNotation) {
    const deleteScenarios = [];

    if (accept_E_notation) {
        deleteScenarios.push(...eNotationDeleteScenarios);
    }
    if (supportScientificNotation) {
        deleteScenarios.push(...scientificNotationDeleteScenarios);
    }

    // These "general" scenarios are less precise, so run the specific notation scenarios first.
    deleteScenarios.push(...generalDeleteScenarios);

    return deleteScenarios;
}

/**
 * Get the keybindings common to Symbolic Number and Number and Unit symtools.
 * @param {boolean} accept_E_notation Whether to accept E notation.
 * @param {boolean} supportScientificNotation Whether to support scientific notation.
 * @returns {Array} The common keybindings.
 */
export function getCommonNumberKeybindings(accept_E_notation, supportScientificNotation) {
    const keybindings = [];

    keybindings.push({ key: '^', command: ['insert', '^{\\placeholder{}}'] }, { key: '[Space]', command: ['insert', '\\;'] });

    if (accept_E_notation) {
        keybindings.push({ key: 'E', command: ['insert', eNotationWithPlaceholder] });
    }

    if (supportScientificNotation) {
        keybindings.push({ key: 'x', command: ['insert', '\\Keyboardx'] }, { key: '*', command: ['insert', '\\cdot'] });
    }

    return keybindings;
}

/**
 * Get the keyboard keys common to Symbolic Number and Number and Unit symtools, including numbers, negate, decimal point, and optional number operators.
 * @param {Object} commonKeys The common keys used across different virtual keyboard layouts.
 * @param {boolean} accept_E_notation Whether to accept E notation.
 * @param {boolean} hide_E_notation Whether to hide the E notation key on the virtual keyboard.
 * @param {boolean} supportScientificNotation Whether to support scientific notation.
 * @returns {Array} The common number keyboard keys.
 */
export function getCommonNumberKeyboardKeys(commonKeys, accept_E_notation, hide_E_notation, supportScientificNotation) {
    const rows = [];
    const { negate } = commonKeys;

    rows.push(['7', '8', '9'], ['4', '5', '6'], ['1', '2', '3'], [negate, '0', '.']);

    // Add optional number operators
    if (supportScientificNotation) {
        rows[0].push({ latex: scientificNotationWithPlaceholder });
    }

    if (accept_E_notation && !hide_E_notation) {
        rows[supportScientificNotation ? 1 : 0].push({ latex: eNotationWithPlaceholder });
    }

    return rows;
}
